import React, { useState } from "react";

// Shared / Employment Components

import EmploymentHeader from "../component/EmploymentHeader";
import EmploymentStats from "../component/EmploymentStats";
import EmploymentFilters from "../component/EmploymentFilters";
import EmploymentToolbar from "../component/EmploymentToolbar";
import EmploymentTable from "../component/EmploymentTable";

import EmployeeDrawer from "../component/EmployeeDrawer";
import EmploymentTypeDrawer from "../component/EmploymentTypeDrawer";
import EditEmploymentDrawer from "../component/EditEmploymentDrawer";
import FilterDrawer from "../component/FilterDrawer";
import EditEmployeeDrawer from "../component/EditEmployeeDrawer";
import EmploymentPagination from "../component/EmploymentPagination";
import ModuleNavbar from "../component/ModuleNavbar";

import useEmployment from "../hooks/useEmployment";
import useEmploymentContracts from "../../Contracts/hooks/useEmploymentContracts";
import UnifiedEmploymentStats from "../component/UnifiedEmploymentStats";
import ContractDetailsDrawer from "../../Contracts/component/ContractDetailsDrawer";
import ContractFormDrawer from "../../Contracts/component/ContractFormDrawer";

const EmploymentPage = () => {
  // Employment Data & Business Logic
  const employment = useEmployment();
  
  // Contracts Data & Business Logic
  const contractContext = useEmploymentContracts();
  
  // Employee Drawer
  const [selectedEmployee, setSelectedEmployee] =
    useState(null);
  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] =
    useState(false);
  // Edit Employee Drawer
  const [isEditEmployeeDrawerOpen, setIsEditEmployeeDrawerOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  // Employment Type Drawer
  const [isEmploymentTypeDrawerOpen, setIsEmploymentTypeDrawerOpen] =
    useState(false);
  // Edit Employment Type Drawer
  const [isEditEmploymentDrawerOpen, setIsEditEmploymentDrawerOpen] =
    useState(false);
  const [selectedEmploymentType, setSelectedEmploymentType] =
    useState(null);
  // Advanced Filter Drawer
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] =
    useState(false);
  // Employee Actions
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEmployeeDrawerOpen(true);
  };
  const handleCloseEmployeeDrawer = () => {
    setIsEmployeeDrawerOpen(false);
    setSelectedEmployee(null);
  };

  // Edit Employee Actions
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsEditEmployeeDrawerOpen(true);
  };

  const handleCloseEditEmployee = () => {
    setIsEditEmployeeDrawerOpen(false);
    setEditingEmployee(null);
  };

  const handleSaveEmployee = async (payload, uuid) => {
    await employment.updateEmployee(uuid, payload);
    handleCloseEditEmployee();
  };

  const handleDeleteEmployee = (employee) => {
    console.log(`Notification sent to manager: Request to delete employee ${employee.full_name} (${employee.employee_id || "No ID"}).`);
  };

  // Employment Type Actions

  const handleOpenEmploymentTypes = () => {
    setIsEmploymentTypeDrawerOpen(true);
  };

  const handleCloseEmploymentTypes = () => {
    setIsEmploymentTypeDrawerOpen(false);
  };

  // Add Employment Type

  const handleAddEmploymentType = () => {
    setSelectedEmploymentType(null);

    setIsEmploymentTypeDrawerOpen(false);

    setIsEditEmploymentDrawerOpen(true);
  };

  // Edit Employment Type

  const handleEditEmploymentType = (
    employmentType
  ) => {
    setSelectedEmploymentType(
      employmentType
    );

    setIsEmploymentTypeDrawerOpen(false);

    setIsEditEmploymentDrawerOpen(true);
  };

  const handleCloseEditEmployment = () => {
    setIsEditEmploymentDrawerOpen(false);

    setSelectedEmploymentType(null);
  };

  const handleSaveEmploymentType = async (
    payload,
    uuid
  ) => {
    try {
      employment.saveEmploymentType(payload, uuid);
      handleCloseEditEmployment();
    } catch (error) {
      console.error(
        "Employment type save failed:",
        error
      );
    }
  };

  // Filter Drawer

  const handleOpenFilters = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilters = () => {
    setIsFilterDrawerOpen(false);
  };

  // Reset Filters

  const handleResetFilters = () => {
    employment.resetFilters();
  };

  // Export

  const handleExport = () => {
    employment.handleExport();
  };



  // Loading State

  if (employment.loading) {
    return (
      <div className="min-h-full flex flex-col bg-slate-50">
        <ModuleNavbar />
        <div className="w-full px-6 py-6 lg:px-8">
          <EmploymentHeader
            onAdd={
              handleOpenEmploymentTypes
            }
          />

          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#147db5]" />

              <p className="mt-3 text-sm text-slate-500">
                Loading employment data...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error State

  if (employment.error) {
    return (
      <div className="min-h-full flex flex-col bg-slate-50">
        <ModuleNavbar />
        <div className="w-full px-6 py-6 lg:px-8">
          <EmploymentHeader
            onAdd={
              handleOpenEmploymentTypes
            }
          />

          <div className="rounded-xl border border-red-200 bg-white p-8 text-center">
            <h2 className="text-sm font-semibold text-red-700">
              Unable to load employment data
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {employment.error}
            </p>

            <button
              type="button"
              onClick={employment.refresh}
              className="mt-4 rounded-lg bg-[#147db5] px-4 py-2 text-sm font-medium text-white hover:bg-[#116d9d]"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Render

  return (
    <div className="min-h-full flex flex-col bg-slate-50">
      <ModuleNavbar />
      <div className="w-full px-6 py-6 lg:px-8 transition-all duration-300 ease-out">

        {/* HEADER */}

        <EmploymentHeader
          onAdd={handleOpenEmploymentTypes}
          onNewContract={() => contractContext.handleOpenCreateForm()}
        />

        {/* STATISTICS */}

        <div className="mt-5">
          <UnifiedEmploymentStats
            stats={employment.stats}
            contractStats={contractContext.stats}
            onActiveTypesClick={handleOpenEmploymentTypes}
          />
        </div>

        {/* FILTERS */}

        <div className="mt-5">
          <EmploymentFilters
            filters={
              employment.filters
            }
            filterOptions={
              employment.filterOptions
            }
            onChange={
              employment.setFilters
            }
            onOpenDrawer={
              handleOpenFilters
            }
          />
        </div>

        {/* TOOLBAR */}

        <div className="mt-4">
          <EmploymentToolbar
            onReset={
              handleResetFilters
            }
            onExport={
              handleExport
            }
            loading={
              employment.loading
            }
          />
        </div>

        {/* EMPLOYEE TABLE CONTENT */}

        <div className="mt-4">
          <EmploymentTable
            employees={employment.paginatedEmployees}
            onEmployeeClick={handleEmployeeClick}
            onEditEmployee={handleEditEmployee}
            onDeleteEmployee={handleDeleteEmployee}
            
            // Unified Contract Actions
            onViewContract={(employee) => {
               const contract = {
                  ...employee,
                  contract_type: employee.contract_type,
                  start_date: employee.start_date,
                  end_date: employee.end_date,
                  duration: employee.duration,
                  status: employee.status,
                  uuid: employee.contract_uuid || employee.uuid
               };
               contractContext.handleOpenDetails(contract);
            }}
            onEditContract={(employee) => {
               const contract = {
                  ...employee,
                  uuid: employee.contract_uuid || employee.uuid
               };
               contractContext.handleOpenEditForm(contract);
            }}
            onRenewContract={(employee) => {
               const contract = {
                  ...employee,
                  uuid: employee.contract_uuid || employee.uuid
               };
               contractContext.handleOpenRenew(contract);
            }}
            loading={employment.loading}
          />
        </div>

        {/* PAGINATION */}

        <EmploymentPagination
          pagination={employment.pagination}
          onPrevious={employment.previousPage}
          onNext={employment.nextPage}
        />
      </div>

      {/* EMPLOYEE DRAWER */}

      <EmployeeDrawer
        employee={
          selectedEmployee
        }
        isOpen={
          isEmployeeDrawerOpen
        }
        onClose={
          handleCloseEmployeeDrawer
        }
        contracts={
          employment.employmentContracts
        }
        schedules={
          employment.workSchedules
        }
        history={
          employment.employmentHistory
        }
        employees={
          employment.employees
        }
      />

      {/* EMPLOYMENT TYPE DRAWER */}

      <EmploymentTypeDrawer
        isOpen={
          isEmploymentTypeDrawerOpen
        }
        onClose={
          handleCloseEmploymentTypes
        }
        onAdd={
          handleAddEmploymentType
        }
        onEdit={
          handleEditEmploymentType
        }
        employmentTypes={
          employment.employmentTypes
        }
        employees={
          employment.employees
        }
      />

      {/* EDIT / ADD EMPLOYMENT TYPE DRAWER */}

      <EditEmploymentDrawer
        isOpen={
          isEditEmploymentDrawerOpen
        }
        onClose={
          handleCloseEditEmployment
        }
        employmentType={
          selectedEmploymentType
        }
        onSave={
          handleSaveEmploymentType
        }
      />

      {/* ADVANCED FILTER DRAWER */}

      <FilterDrawer
        isOpen={
          isFilterDrawerOpen
        }
        onClose={
          handleCloseFilters
        }
        filters={
          employment.filters
        }
        filterOptions={
          employment.filterOptions
        }
        onApply={
          employment.setFilters
        }
      />

      <EditEmployeeDrawer
        isOpen={isEditEmployeeDrawerOpen}
        onClose={handleCloseEditEmployee}
        employee={editingEmployee}
        onSave={handleSaveEmployee}
        departments={employment.departments}
        employmentTypes={employment.employmentTypes}
        workSchedules={employment.workSchedules}
      />

      {/* CONTRACT DRAWERS */}
      
      {contractContext.isDetailsDrawerOpen && contractContext.selectedContract && (
        <ContractDetailsDrawer
          isOpen={contractContext.isDetailsDrawerOpen}
          onClose={contractContext.handleCloseDetails}
          contract={contractContext.selectedContract}
          onEdit={() => {
             contractContext.handleCloseDetails();
             contractContext.handleOpenEditForm(contractContext.selectedContract);
          }}
        />
      )}

      {contractContext.isFormDrawerOpen && (
        <ContractFormDrawer
          isOpen={contractContext.isFormDrawerOpen}
          onClose={contractContext.handleCloseForm}
          initialData={contractContext.editingContract}
          isRenewing={false}
          onSubmit={async (payload) => {
            await contractContext.handleSaveContract(payload);
            employment.refresh();
          }}
          employees={employment.employees}
        />
      )}

      {contractContext.isRenewDrawerOpen && (
        <ContractFormDrawer
          isOpen={contractContext.isRenewDrawerOpen}
          onClose={contractContext.handleCloseRenew}
          initialData={contractContext.contractToRenew}
          isRenewing={true}
          onSubmit={async (payload) => {
            await contractContext.handleRenewContractSubmit(payload);
            employment.refresh();
          }}
          employees={employment.employees}
        />
      )}
    </div>
  );
};

export default EmploymentPage;